// CouncilStatus.jsx — terminal outcome banner for Council v2.
//
// Shows the truthful terminal status (complete|partial|cancelled|error), the
// persistence state (unsaved), cost (unknown != free), duration and the export
// actions. Status is INDEPENDENT of phase: a run can end mid-review as partial.

import { AlertTriangle, CheckCircle2, Clock, Download, Ban, XCircle } from 'lucide-react';
import { formatCost, statusLabel } from '../../lib/councilProtocol.js';
import { CopyButton } from './CouncilOutput.jsx';

function iconFor(status) {
  if (status === 'complete') return <CheckCircle2 size={13} className="text-apolo-success" aria-hidden="true" />;
  if (status === 'partial') return <AlertTriangle size={13} className="text-apolo-warning" aria-hidden="true" />;
  if (status === 'cancelled') return <Ban size={13} className="text-apolo-textTertiary" aria-hidden="true" />;
  return <XCircle size={13} className="text-apolo-error" aria-hidden="true" />;
}

export default function CouncilStatus({ terminal, markdown, onExport }) {
  if (!terminal) return null;
  const { status, saved, durationMs } = terminal;
  const cost = formatCost(terminal);

  return (
    <section
      aria-live="polite"
      aria-label="Resultado da deliberação"
      className="flex flex-col gap-2 rounded-xl border border-apolo-borderSubtle bg-apolo-surface1 px-3 py-2.5"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-apolo-textPrimary">
          {iconFor(status)}
          {statusLabel(status)}
        </span>
        <span className="inline-flex items-center gap-1 text-[10.5px] text-apolo-textTertiary" title="Custo informado pelo backend; desconhecido quando não há preço">
          {cost}
        </span>
        {typeof durationMs === 'number' && (
          <span className="inline-flex items-center gap-1 text-[10.5px] text-apolo-textTertiary">
            <Clock size={10} aria-hidden="true" /> {(durationMs / 1000).toFixed(1)}s
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">
          <CopyButton text={markdown} label="Copiar" />
          <button
            type="button"
            onClick={onExport}
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-1 focus-visible:ring-apolo-accentRing"
          >
            <Download size={11} aria-hidden="true" /> Exportar .md
          </button>
        </div>
      </div>

      {status === 'partial' && (
        <p className="text-[10.5px] leading-relaxed text-apolo-warning">
          Resultado parcial: parte das respostas foi preservada, mas uma ou mais fases não concluíram.
        </p>
      )}
      {status === 'cancelled' && (
        <p className="text-[10.5px] leading-relaxed text-apolo-textSecondary">
          Deliberação cancelada. O conteúdo já recebido foi preservado. Cancelar não garante estorno de cobranças do provedor.
        </p>
      )}
      {saved == null && <p className="text-xs text-apolo-textTertiary">Gravação no histórico ainda não confirmada.</p>}
      {saved === false && (
        <p role="alert" className="inline-flex items-center gap-1.5 text-[10.5px] text-apolo-warning">
          <AlertTriangle size={11} aria-hidden="true" /> Não foi possível salvar esta deliberação no histórico.
        </p>
      )}
    </section>
  );
}
